import { createApiRef } from '@backstage/core-plugin-api';

export const azurePolicyApiRef = createApiRef<AzurePolicyApi>({
  id: 'az.policy.service',
});

/**
 * Request body for fetching policy states.
 */
export interface PolicyStatesRequest {
  /** List of Azure subscription IDs to fetch policy states from */
  subscriptionIds?: string[];
  /** List of Azure management group IDs to fetch policy states from */
  managementGroupIds?: string[];
}

/**
 * Response containing policy compliance states.
 */
export interface PolicyStatesResponse {
  /** List of policy states retrieved from Azure */
  policyStates: PolicyState[];
  /** Total number of policy states retrieved */
  totalCount: number;
}

/**
 * Represents a single policy compliance state from Azure.
 */
export interface PolicyState {
  /** The resource ID that the policy state is for */
  resourceId?: string;
  /** The policy definition ID */
  policyDefinitionId?: string;
  /** The policy definition name */
  policyDefinitionName?: string;
  /** The policy definition reference ID (used in policy initiatives) */
  policyDefinitionReferenceId?: string;
  policyDefinitionDisplayName?: string;
  /** The policy set definition ID (for initiatives) */
  policySetDefinitionId?: string;
  /** The policy set definition display name (enriched from policy assignment) */
  policySetDefinitionDisplayName?: string;
  /** The display name (enriched from policy assignment - works for both initiatives and individual policies) */
  displayName?: string;
  /** The compliance state of the resource */
  complianceState?: 'Compliant' | 'NonCompliant' | 'Unknown';
  /** The timestamp when the policy state was recorded */
  timestamp?: string;
  /** The subscription ID */
  subscriptionId?: string;
  /** The resource group name */
  resourceGroup?: string;
  /** The resource type */
  resourceType?: string;
  /** The resource location */
  resourceLocation?: string;
  /** The policy assignment ID */
  policyAssignmentId?: string;
  /** The policy assignment name */
  policyAssignmentName?: string;
  /** Additional properties from the policy state */
  additionalProperties?: Record<string, any>;
}

/**
 * Represents an Azure subscription under a management group.
 */
export interface AzureManagementGroupSubscription {
  /** The subscription ID */
  id: string;
  /** The subscription name (GUID) */
  name: string;
  /** The display name of the subscription */
  displayName: string;
  /** The state of the subscription */
  state: string;
}

/**
 * Represents an Azure management group with its child subscriptions.
 */
export interface AzureManagementGroupWithSubscriptions {
  /** The management group ID */
  id: string;
  /** The display name of the management group */
  displayName: string;
  /** The type of the management group */
  type?: string;
  /** Child subscriptions under this management group */
  subscriptions: AzureManagementGroupSubscription[];
}

/**
 * Response containing available Azure management groups with their child subscriptions.
 */
export interface AzureManagementGroupsWithSubscriptionsResponse {
  /** List of available management groups with subscriptions */
  managementGroups: AzureManagementGroupWithSubscriptions[];
  /** Standalone subscriptions not under any management group */
  standaloneSubscriptions: AzureManagementGroupSubscription[];
}

/**
 * API interface for Azure Policy operations.
 */
export interface AzurePolicyApi {
  /**
   * Fetch policy compliance states from Azure with policy definition display names.
   * @param request - The request containing subscription IDs and/or management group IDs
   * @returns Promise resolving to policy states response
   */
  getPolicyStates(request: PolicyStatesRequest): Promise<PolicyStatesResponse>;

  /**
   * Get available Azure management groups with their child subscriptions.
   * @returns Promise resolving to management groups with subscriptions response
   */
  getManagementGroupsWithSubscriptions(): Promise<AzureManagementGroupsWithSubscriptionsResponse>;
}
